import {
	ACCEPTED_PHOTO_MIME_TYPES,
	MAX_PHOTO_FILE_BYTES,
	type PhotoFileValue,
	splitPhotoDataUrl
} from "~/helpers/photoFile";

export type ReadProfileImageResult =
	| { error: string; value?: undefined }
	| { error?: undefined; value: PhotoFileValue };

export interface SquareCrop {
	size: number;
	sx: number;
	sy: number;
}

export const AVATAR_MAX_DIMENSION = 512;
const AVATAR_OUTPUT_QUALITY = 0.9;
const MAX_PHOTO_FILE_MB = Math.round(MAX_PHOTO_FILE_BYTES / (1024 * 1024));
const UNSUPPORTED_TYPE_ERROR = "Please choose a JPEG, PNG, or WEBP image";
const OVERSIZE_ERROR = `Image must be smaller than ${MAX_PHOTO_FILE_MB} MB`;
const PROCESS_ERROR = "Could not process the selected image";
const READ_ERROR = "Could not read the selected file";

export const validateProfileImageFile = (file: File): null | string => {
	if (!ACCEPTED_PHOTO_MIME_TYPES.includes(file.type)) return UNSUPPORTED_TYPE_ERROR;
	if (file.size > MAX_PHOTO_FILE_BYTES) return OVERSIZE_ERROR;
	return null;
};

export const computeCenterSquareCrop = (width: number, height: number): SquareCrop => {
	const size = Math.min(width, height);
	return { size, sx: Math.floor((width - size) / 2), sy: Math.floor((height - size) / 2) };
};

export const resolveAvatarOutputMime = (inputMime: string): string =>
	inputMime === "image/png" ? "image/png" : "image/jpeg";

export const resolveAvatarTargetSize = (cropSize: number): number => Math.min(cropSize, AVATAR_MAX_DIMENSION);

const approximateBase64Bytes = (base64: string): number => Math.floor((base64.length * 3) / 4);

const readDataUrl = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => reject(reader.error ?? new Error(READ_ERROR));
		reader.readAsDataURL(file);
	});

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error(PROCESS_ERROR));
		image.src = dataUrl;
	});

const readProfileImageFile = async (file: File): Promise<ReadProfileImageResult> => {
	const validationError = validateProfileImageFile(file);
	if (validationError) return { error: validationError };

	let dataUrl: string;
	try {
		dataUrl = await readDataUrl(file);
	} catch {
		return { error: READ_ERROR };
	}

	let image: HTMLImageElement;
	try {
		image = await loadImage(dataUrl);
	} catch {
		return { error: PROCESS_ERROR };
	}

	const { size, sx, sy } = computeCenterSquareCrop(image.naturalWidth, image.naturalHeight);
	if (size <= 0) return { error: PROCESS_ERROR };

	const targetSize = resolveAvatarTargetSize(size);
	const canvas = document.createElement("canvas");
	canvas.width = targetSize;
	canvas.height = targetSize;
	const context = canvas.getContext("2d");
	if (!context) return { error: PROCESS_ERROR };
	context.drawImage(image, sx, sy, size, size, 0, 0, targetSize, targetSize);

	const outputMime = resolveAvatarOutputMime(file.type);
	const value = splitPhotoDataUrl(canvas.toDataURL(outputMime, AVATAR_OUTPUT_QUALITY));
	if (!value) return { error: PROCESS_ERROR };
	if (approximateBase64Bytes(value.image) > MAX_PHOTO_FILE_BYTES) return { error: OVERSIZE_ERROR };

	return { value };
};

export default readProfileImageFile;
